/**
 * Report edit mode's two drags onto the tab row (components/report/tab-strip.blade.php). Report only:
 * the Site Analytics page never loads it, and site-analytics-arrange.js, which both pages share, is
 * not changed by it. Mixed into the page's scope (report-page.js), which starts and stops it.
 *
 * 1. A card dragged onto another tab. While a card of the open tab is dragged (SortableJS, through
 *    the arrange module), the tab button under the pointer is the drop target: outlined inside its
 *    own box, with a "Move to <tab>" hint above it, so the target is never told by colour alone.
 *    Let go there, the card is put back where the drag took it from, before SortableJS reads where
 *    it ended (as the arrange module puts back a drag let go over the pinned headers), and
 *    Show::moveCardToTab() moves it to the end of that tab: the card's own Move → "Move to <tab>"
 *    path, so it is one change in the working copy, undone by Undo and said in the arrange region.
 *    The open tab stays open. The open tab's own button is no target: a card let go there is put
 *    back and nothing happens. Held on "More (N)" for MORE_OPEN_DELAY, the disclosure opens and its
 *    tabs are targets too; it closes again when the drag ends. An Add chart panel entry let go on a
 *    tab is put back into the panel, which comes back whole (DROP_CANCEL_EVENT): nothing is added,
 *    and no tab is shown as a target for it.
 *
 * 2. A tab dragged to a new place in the row. Pointer events, not SortableJS: a press on a tab only
 *    becomes a drag once the pointer has moved TAB_DRAG_THRESHOLD px, so a click still opens the tab
 *    (the click that ends a drag is swallowed), and the options button, outside the tablist, is
 *    never part of it. While dragging, a bar between two tabs marks where the tab lands; let go,
 *    Show::moveTabDragged() moves it there, Move earlier and Move later's move. Let go away from the
 *    row (TAB_DROP_BAND), where it already is, or after Escape, nothing moves. Only the tabs the row
 *    shows drag and take a drop; the ones under "More" do not. The press handler is on the tab
 *    buttons in edit mode only, so a reader's tabs never drag.
 *
 * Both are pointer enhancements over keyboard paths that stay as they are (the card's Move menu,
 * the tab options' Move earlier and Move later), so nothing here adds a role or a tab stop, and the
 * tablist's arrow keys are untouched. The marker and the hint are aria-hidden; each move is said in
 * the arrange region by the server's answer, as the menus' moves are.
 *
 * Tab and card ids are read off the elements (data-report-tab, data-report-tab-more-item and the
 * card's data-site-analytics-card), never written into an expression.
 */
import { DROP_CANCEL_EVENT, GRAB_EVENT } from './site-analytics-arrange';

/** How far, in px, a pressed tab moves before it is dragged rather than clicked. */
export const TAB_DRAG_THRESHOLD = 6;

/** How long, in ms, a dragged card rests on "More (N)" before it opens. */
export const MORE_OPEN_DELAY = 500;

/** How far above or below the row, in px, a dragged tab may be let go and still land. */
export const TAB_DROP_BAND = 48;

/** SortableJS's own DOM event for a drag that has started, bubbling from the list it came from. */
const SORTABLE_START_EVENT = 'start';

/** And for a drag that is over, however it ended. */
const SORTABLE_END_EVENT = 'end';

/** Tailwind's `hidden`, which rp_layoutTabs() puts on a tab the row moved under "More". */
const TAB_HIDDEN = 'hidden';

/**
 * Where a dragged tab lands in the report's whole tab order: `order` holds every tab id in order
 * (the ones under "More" too), `afterId` the tab it is let go after ('' for the front). Returns the
 * index it takes once it has left its place (what ReportArrangement::moveTabBy() counts to), or null
 * when that is where it already is.
 *
 * @param {string[]} order
 * @param {string} draggedId
 * @param {string} afterId
 * @returns {?number}
 */
export function tabDropIndex(order, draggedId, afterId) {
    const from = Array.isArray(order) ? order.indexOf(draggedId) : -1;

    if (from < 0 || afterId === draggedId) return null;

    const rest = order.filter((id) => id !== draggedId);
    let to = 0;

    if (afterId !== '') {
        const after = rest.indexOf(afterId);

        if (after < 0) return null;

        to = after + 1;
    }

    return to === from ? null : to;
}

/**
 * Which gap of the row the pointer is at: how many of the shown tabs' middles lie left of `x`, given
 * each shown tab's left and right edge in order. 0 is before the first tab, the count after the last.
 *
 * @param {number} x
 * @param {Array<{left: number, right: number}>} boxes
 * @returns {number}
 */
export function tabGapAt(x, boxes) {
    return (Array.isArray(boxes) ? boxes : []).filter((box) => (box.left + box.right) / 2 < x).length;
}

/** A sentence with `:tab` in it, filled with a tab's name, which may hold any character. */
function withName(template, name) {
    return String(template).split(':tab').join(name);
}

export default function ReportTabDrag() {
    /** The page's root element, kept from rp_tabDragInit(). */
    let root = null;
    /** The window listener that notices a card drag starting. */
    let onStart = null;

    /**
     * A card (or panel entry) being dragged: the item, the list and node it came from, whether it is
     * a card of the open tab, the tab button under the pointer (`under`), the one a drop moves it to
     * (`target`, never the open tab), the "More" timer, whether this drag opened "More", the
     * listeners it added, and whether it has been let go.
     */
    let carry = null;

    /**
     * A tab pressed in edit mode: the button, its id and pointer, where the press was, whether it
     * became a drag, where it would land (`toIndex`, null for nowhere), whether Escape called it off,
     * and the listeners it added.
     */
    let press = null;

    const listen = (list, type, handler, options) => {
        window.addEventListener(type, handler, options);
        list.push(() => window.removeEventListener(type, handler, options));
    };

    /** The next click, which ends a drag on the tab it started on, is not a press of that tab. */
    const swallowNextClick = () => {
        const swallow = (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
        };

        window.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
    };

    const strip = () => root?.querySelector?.('[data-report-tab-strip]') ?? null;

    return {
        /** Started from the page's init(): a card drag is noticed on SortableJS's `start`. */
        rp_tabDragInit(pageRoot) {
            root = pageRoot ?? null;

            if (onStart !== null) return;

            onStart = (event) => this.rp_carryStart(event);
            window.addEventListener(SORTABLE_START_EVENT, onStart);
        },

        rp_tabDragDestroy() {
            if (onStart !== null) window.removeEventListener(SORTABLE_START_EVENT, onStart);

            onStart = null;
            this.rp_carryFinish();
            this.rp_tabPressFinish();
            root = null;
        },

        /**
         * SortableJS's `start`: a card of the open tab's grid, or an Add chart panel entry, has left
         * its place. Followed only on a report of several tabs; the tabs are measured as it moves.
         */
        rp_carryStart(event) {
            const item = event?.item;
            const from = event?.from;
            const grid = root?.querySelector?.('[data-site-analytics-grid]');
            const isCard = Boolean(grid) && from === grid;
            const isEntry = Boolean(from?.closest?.('[data-report-palette]'));

            if (! item || ! from || (! isCard && ! isEntry) || ! strip()?.querySelector('[role="tablist"]')) return;

            this.rp_carryFinish();

            carry = { item, from, next: item.nextSibling, isCard, under: null, target: null, moreTimer: null, isMoreOpened: false, isReleased: false, listeners: [] };

            const track = (moved) => this.rp_carryTrack(moved);
            const release = (released) => this.rp_carryRelease(released);

            // Capture on window: SortableJS stops a dragover at the list it is over, and the release
            // must be seen before its own handlers read where the item ended.
            listen(carry.listeners, 'dragover', track, true);
            listen(carry.listeners, 'pointermove', track, true);
            listen(carry.listeners, 'touchmove', track, { capture: true, passive: true });
            listen(carry.listeners, 'drop', release, true);
            listen(carry.listeners, 'dragend', release, true);
            listen(carry.listeners, 'pointerup', release, true);
            listen(carry.listeners, 'touchend', release, true);
            listen(carry.listeners, SORTABLE_END_EVENT, () => this.rp_carryFinish(), false);
        },

        /**
         * The tab button (or "More" entry) at a point, and whether a drop there moves the carried
         * card: a card, onto a tab other than the open one.
         *
         * @returns {{under: ?Element, target: ?Element, more: ?Element}}
         */
        rp_carryAt(x, y) {
            const row = strip();
            const element = row && Number.isFinite(x) && Number.isFinite(y) ? document.elementFromPoint(x, y) : null;
            const found = element?.closest?.('[data-report-tab], [data-report-tab-more-item]') ?? null;
            const under = found && row.contains(found) && ! found.classList.contains(TAB_HIDDEN) ? found : null;
            const isOpen = under?.getAttribute('aria-selected') === 'true';
            const more = element?.closest?.('[data-report-tab-more-trigger]') ?? null;

            return { under, target: under && carry?.isCard && ! isOpen ? under : null, more: more && row.contains(more) ? more : null };
        },

        /** The pointer moved while carrying: the target under it is shown, and a drop there allowed. */
        rp_carryTrack(event) {
            if (! carry || carry.isReleased) return;

            const point = event.touches?.[0] ?? event;
            const { under, target, more } = this.rp_carryAt(point.clientX, point.clientY);

            carry.under = under;

            // A native drag is only let go (`drop`) where a dragover was cancelled.
            if (target && event.type === 'dragover') {
                event.preventDefault();

                if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
            }

            if (target !== carry.target) {
                carry.target?.removeAttribute('data-report-tab-drop-target');
                carry.target = target;
            }

            // Set on every move: a render during the drag puts back the server's markup.
            if (target) target.setAttribute('data-report-tab-drop-target', '');

            this.rp_showDropHint(target);
            this.rp_carryWaitOnMore(more);
        },

        /**
         * A card resting on "More (N)" opens it after MORE_OPEN_DELAY, so the tabs under it can take
         * the drop; moving off it before then cancels the wait.
         */
        rp_carryWaitOnMore(more) {
            if (! carry?.isCard || ! more) {
                clearTimeout(carry?.moreTimer);

                if (carry) carry.moreTimer = null;

                return;
            }

            if (carry.moreTimer !== null || carry.isMoreOpened) return;

            carry.moreTimer = setTimeout(() => {
                if (! carry) return;

                carry.moreTimer = null;

                const disclosure = more.closest('[data-report-tab-more]');
                const state = disclosure ? window.Alpine?.$data?.(disclosure) : null;

                if (state && ! state.open) {
                    state.open = true;
                    carry.isMoreOpened = true;
                }
            }, MORE_OPEN_DELAY);
        },

        /**
         * The drag let go, before SortableJS handles it. On a tab button, the item goes back to the
         * node it started before, in the list it came from, so SortableJS finds it where it started
         * and records nothing; then a card let go on another tab moves there, on the server. Anywhere
         * else the drop is SortableJS's, as it was.
         */
        rp_carryRelease(event) {
            if (! carry || carry.isReleased) return;

            // A native drag's end is placed by its last dragover; the rest by where they let go.
            if (event.type !== 'dragend') {
                const point = event.changedTouches?.[0] ?? event;
                const { under, target } = this.rp_carryAt(point.clientX, point.clientY);

                carry.under = under;
                carry.target = target;
            }

            if (! carry.under) return;

            const { item, from, next, target } = carry;
            const cardId = item.getAttribute?.('data-site-analytics-card') ?? '';
            const tabId = target?.getAttribute('data-report-tab') ?? target?.getAttribute('data-report-tab-more-item') ?? '';

            carry.isReleased = true;

            if (item.parentNode !== from || item.nextSibling !== next) {
                if (next && next.parentNode === from) {
                    from.insertBefore(item, next);
                } else {
                    from.appendChild(item);
                }
            }

            window.dispatchEvent(new CustomEvent(DROP_CANCEL_EVENT, { detail: { item, from } }));

            this.rp_clearDropTarget();

            if (carry.isCard && cardId !== '' && tabId !== '') this.$wire.moveCardToTab(cardId, tabId, true);
        },

        /** The drag is over, however it ended: its listeners, the target's outline and hint, and a "More" it opened. */
        rp_carryFinish() {
            if (! carry) return;

            const done = carry;

            carry = null;
            clearTimeout(done.moreTimer);
            done.listeners.forEach((remove) => remove());
            done.target?.removeAttribute('data-report-tab-drop-target');
            this.rp_clearDropTarget();

            if (done.isMoreOpened) {
                const disclosure = strip()?.querySelector('[data-report-tab-more]');
                const state = disclosure ? window.Alpine?.$data?.(disclosure) : null;

                if (state) state.open = false;
            }
        },

        /** No tab is shown as a target any more. */
        rp_clearDropTarget() {
            strip()?.querySelectorAll('[data-report-tab-drop-target]').forEach((element) => element.removeAttribute('data-report-tab-drop-target'));
            this.rp_showDropHint(null);
        },

        /**
         * The "Move to <tab>" hint beside a target: above a tab of the row (the dragged card's image
         * hangs below and right of the pointer), left of an entry under "More", whose panel hangs
         * over the row's right end. Hidden for no target.
         */
        rp_showDropHint(target) {
            const row = strip();
            const hint = row?.querySelector('[data-report-tab-drop-hint]');

            if (! hint) return;

            if (! target) {
                hint.classList.add(TAB_HIDDEN);

                return;
            }

            const name = target.getAttribute('title') || target.textContent.trim();

            hint.textContent = withName(hint.getAttribute('data-template') ?? 'Move to :tab', name);
            hint.classList.remove(TAB_HIDDEN);

            const box = row.getBoundingClientRect();
            const at = target.getBoundingClientRect();
            const size = hint.getBoundingClientRect();
            const isMoreEntry = target.hasAttribute('data-report-tab-more-item');
            const left = isMoreEntry ? at.left - size.width - 8 : at.left;
            const top = isMoreEntry ? at.top + (at.height - size.height) / 2 : at.top - size.height - 6;

            hint.style.left = `${Math.round(left - box.left - (row.clientLeft || 0))}px`;
            hint.style.top = `${Math.round(top - box.top - (row.clientTop || 0))}px`;
        },

        /**
         * A tab of the row pressed in edit mode (the tab strip's `x-on:pointerdown`, drawn only while
         * editing). Nothing happens yet: a press that does not move is the tab's click.
         */
        rp_tabPress(event) {
            const tab = event?.currentTarget;

            if (! tab || event.button !== 0 || event.isPrimary === false || this.rp_tabRenaming || tab.classList.contains(TAB_HIDDEN)) return;

            this.rp_tabPressFinish();

            press = { tab, id: tab.getAttribute('data-report-tab') ?? '', pointerId: event.pointerId, x: event.clientX, y: event.clientY, isDragging: false, toIndex: null, isCancelled: false, listeners: [] };

            listen(press.listeners, 'pointermove', (moved) => this.rp_tabPressMove(moved), true);
            listen(press.listeners, 'pointerup', (released) => this.rp_tabPressRelease(released), true);
            listen(press.listeners, 'pointercancel', () => this.rp_tabPressFinish(), true);
            listen(press.listeners, 'keydown', (key) => this.rp_tabPressKey(key), true);
        },

        rp_tabPressMove(event) {
            if (! press || event.pointerId !== press.pointerId || press.isCancelled) return;

            if (! press.isDragging) {
                if (Math.hypot(event.clientX - press.x, event.clientY - press.y) < TAB_DRAG_THRESHOLD) return;

                press.isDragging = true;
                press.tab.setAttribute('data-report-tab-dragging', '');
                document.body.classList.add('report-tab-dragging');
                // A drag is a grab: every arrange menu, the tab's options and "More" included, closes.
                window.dispatchEvent(new CustomEvent(GRAB_EVENT, { detail: { item: press.tab } }));
            }

            this.rp_tabDragAt(event.clientX, event.clientY);
        },

        /**
         * Where the dragged tab would land with the pointer here: the gap of the row it is at, marked
         * with the bar, or nowhere (no bar) away from the row or where the tab already is.
         */
        rp_tabDragAt(x, y) {
            const row = strip();
            const list = row?.querySelector('[role="tablist"]');
            const marker = row?.querySelector('[data-report-tab-drop-marker]');

            if (! press || ! list) return;

            const tabs = [...list.querySelectorAll('[role="tab"]')];
            const shown = tabs.filter((tab) => ! tab.classList.contains(TAB_HIDDEN));
            const boxes = shown.map((tab) => tab.getBoundingClientRect());
            const box = row.getBoundingClientRect();
            const isNearRow = y >= box.top - TAB_DROP_BAND && y <= box.bottom + TAB_DROP_BAND;
            const gap = tabGapAt(x, boxes);
            const afterId = gap === 0 ? '' : shown[gap - 1].getAttribute('data-report-tab') ?? '';
            const order = tabs.map((tab) => tab.getAttribute('data-report-tab') ?? '');

            press.toIndex = isNearRow ? tabDropIndex(order, press.id, afterId) : null;

            if (! marker) return;

            if (press.toIndex === null) {
                marker.classList.add(TAB_HIDDEN);

                return;
            }

            // Between the tab before the gap and the one after it; past the last, just after it.
            const listGap = parseFloat(window.getComputedStyle(list).columnGap) || 0;
            const edge = gap < boxes.length
                ? boxes[gap].left - listGap / 2
                : boxes[gap - 1].right + (parseFloat(window.getComputedStyle(shown[gap - 1]).marginRight) || 0) + listGap / 2;

            marker.style.left = `${Math.round(edge - box.left - (row.clientLeft || 0))}px`;
            marker.classList.remove(TAB_HIDDEN);
        },

        rp_tabPressRelease(event) {
            if (! press || event.pointerId !== press.pointerId) return;

            const { id, isDragging, isCancelled, toIndex } = press;

            this.rp_tabPressFinish();

            if (! isDragging) return;

            swallowNextClick();

            if (! isCancelled && toIndex !== null && id !== '') this.$wire.moveTabDragged(id, toIndex);
        },

        /** Escape while dragging calls the drag off; the release that follows still opens nothing. */
        rp_tabPressKey(event) {
            if (! press?.isDragging || event.key !== 'Escape') return;

            event.preventDefault();
            event.stopPropagation();
            press.isCancelled = true;
            press.toIndex = null;
            strip()?.querySelector('[data-report-tab-drop-marker]')?.classList.add(TAB_HIDDEN);
        },

        /** The press is over: its listeners, the dragged look and the bar. */
        rp_tabPressFinish() {
            if (! press) return;

            const done = press;

            press = null;
            done.listeners.forEach((remove) => remove());
            done.tab.removeAttribute('data-report-tab-dragging');
            document.body?.classList.remove('report-tab-dragging');
            strip()?.querySelector('[data-report-tab-drop-marker]')?.classList.add(TAB_HIDDEN);
        },
    };
}
